import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import UpperCaseInput from '../components/UpperCaseInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Users, Search, Phone, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_FORM = {
  razon_social: '',
  alias: '',
  rut: '',
  rut_dv: '',
  giro: '',
  direccion: '',
  comuna: '',
  ciudad: '',
  contacto_nombre: '',
  contacto_telefono: ''
};

// Función para formatear RUT (xx.xxx.xxx-x)
const formatRut = (value) => {
  // Remover todo excepto números y K
  let clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
  
  if (clean.length === 0) return '';
  
  // Separar cuerpo y dígito verificador
  let dv = clean.slice(-1);
  let body = clean.slice(0, -1);
  
  // Formatear el cuerpo con puntos
  let formatted = '';
  let count = 0;
  for (let i = body.length - 1; i >= 0; i--) {
    if (count > 0 && count % 3 === 0) {
      formatted = '.' + formatted;
    }
    formatted = body[i] + formatted;
    count++;
  }
  
  // Si hay dígito verificador, agregar guión
  if (clean.length > 1) {
    return `${formatted}-${dv}`;
  }
  
  return formatted || dv;
};

// Función para validar RUT chileno
const validarRut = (rutCompleto) => {
  if (!rutCompleto || rutCompleto.length < 3) return false;
  
  const rutLimpio = rutCompleto.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rutLimpio.length < 2) return false;
  
  const cuerpo = rutLimpio.slice(0, -1);
  const dv = rutLimpio.slice(-1);
  
  if (!/^\d+$/.test(cuerpo)) return false;
  
  let suma = 0;
  let multiplicador = 2;
  
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  
  const resto = suma % 11;
  const dvCalculado = 11 - resto;
  
  let dvEsperado;
  if (dvCalculado === 11) dvEsperado = '0';
  else if (dvCalculado === 10) dvEsperado = 'K';
  else dvEsperado = dvCalculado.toString();
  
  return dv === dvEsperado;
};

// Función para extraer RUT sin DV y DV por separado
const separarRut = (rutFormateado) => {
  const limpio = rutFormateado.replace(/[^0-9kK]/g, '').toUpperCase();
  if (limpio.length < 2) return { rut: limpio, rut_dv: '' };
  return {
    rut: limpio.slice(0, -1),
    rut_dv: limpio.slice(-1)
  };
};

// Función para unir RUT con DV
const unirRut = (rut, rut_dv) => {
  if (!rut) return '';
  return formatRut(rut + (rut_dv || ''));
};

// Función para formatear teléfono chileno (+56 9 xxxx xxxx)
const formatTelefono = (value) => {
  // Remover todo excepto números
  let clean = value.replace(/[^0-9]/g, '');
  
  // Si empieza con 56, removerlo para el formateo
  if (clean.startsWith('56')) {
    clean = clean.slice(2);
  }
  
  // Si empieza con 9, mantenerlo
  if (!clean.startsWith('9') && clean.length > 0) {
    // Si tiene más de 8 dígitos y no empieza con 9, puede ser que el usuario puso el código completo
    if (clean.length > 8) {
      clean = clean.slice(-9); // Tomar los últimos 9 dígitos
    }
  }
  
  // Limitar a 9 dígitos (9 + 8 dígitos)
  clean = clean.slice(0, 9);
  
  if (clean.length === 0) return '';
  
  // Formatear: +56 9 xxxx xxxx
  let formatted = '+56';
  
  if (clean.length >= 1) {
    formatted += ' ' + clean.slice(0, 1); // El 9
  }
  if (clean.length > 1) {
    formatted += ' ' + clean.slice(1, 5); // Primeros 4 dígitos
  }
  if (clean.length > 5) {
    formatted += ' ' + clean.slice(5, 9); // Últimos 4 dígitos
  }
  
  return formatted;
};

const Clients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [justification, setJustification] = useState('');
  const [filterText, setFilterText] = useState('');
  
  // Estado para el RUT formateado (para mostrar en el input)
  const [rutDisplay, setRutDisplay] = useState('');
  const [telefonoDisplay, setTelefonoDisplay] = useState('');
  const [rutError, setRutError] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    if (!filterText) return clients;
    const term = filterText.toLowerCase();
    return clients.filter(c =>
      c.alias?.toLowerCase().includes(term) ||
      c.razon_social?.toLowerCase().includes(term) ||
      c.rut?.toLowerCase().includes(term) ||
      c.ciudad?.toLowerCase().includes(term)
    );
  }, [clients, filterText]);

  const handleRutChange = (e) => {
    const value = e.target.value;
    const formatted = formatRut(value);
    setRutDisplay(formatted);
    
    // Separar para guardar en formData
    const { rut, rut_dv } = separarRut(formatted);
    setFormData({ ...formData, rut, rut_dv });
    
    // Validar RUT
    if (formatted.length >= 3) {
      if (validarRut(formatted)) {
        setRutError('');
      } else {
        setRutError('RUT inválido');
      }
    } else {
      setRutError('');
    }
  };

  const handleTelefonoChange = (e) => {
    const value = e.target.value;
    const formatted = formatTelefono(value);
    setTelefonoDisplay(formatted);
    setFormData({ ...formData, contacto_telefono: formatted });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    // Validar RUT antes de enviar
    const rutCompleto = formData.rut + formData.rut_dv;
    if (!validarRut(rutCompleto)) {
      toast.error('El RUT ingresado no es válido');
      return;
    }
    
    try {
      await axios.post(`${API}/clients`, formData);
      toast.success('Cliente creado exitosamente');
      setIsCreateModalOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cliente');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!justification.trim()) {
      toast.error('La justificación es obligatoria');
      return;
    }
    
    // Validar RUT antes de enviar
    const rutCompleto = formData.rut + formData.rut_dv;
    if (!validarRut(rutCompleto)) {
      toast.error('El RUT ingresado no es válido');
      return;
    }
    
    try {
      await axios.put(`${API}/clients/${selectedClient.id}`, { 
        justification, 
        data: formData 
      });
      toast.success('Cliente actualizado');
      setIsEditModalOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setJustification('');
    setSelectedClient(null);
    setRutDisplay('');
    setTelefonoDisplay('');
    setRutError('');
  };

  const openEditModal = (client) => {
    setSelectedClient(client);
    setFormData({
      razon_social: client.razon_social || '',
      alias: client.alias || '',
      rut: client.rut || '',
      rut_dv: client.rut_dv || '',
      giro: client.giro || '',
      direccion: client.direccion || '',
      comuna: client.comuna || '',
      ciudad: client.ciudad || '',
      contacto_nombre: client.contacto_nombre || '',
      contacto_telefono: client.contacto_telefono || ''
    });
    // Setear displays
    setRutDisplay(unirRut(client.rut, client.rut_dv));
    setTelefonoDisplay(client.contacto_telefono || '');
    setIsEditModalOpen(true);
  };

  const openCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const renderForm = () => (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2 col-span-2">
        <Label>Razón Social *</Label>
        <UpperCaseInput 
          value={formData.razon_social} 
          onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })} 
          required 
        />
      </div>
      <div className="space-y-2 col-span-2">
        <Label>Alias *</Label>
        <UpperCaseInput 
          value={formData.alias} 
          onChange={(e) => setFormData({ ...formData, alias: e.target.value })} 
          required 
          placeholder="Nombre corto del cliente"
        />
      </div>
      <div className="space-y-2 col-span-2">
        <Label>RUT *</Label>
        <Input 
          value={rutDisplay} 
          onChange={handleRutChange} 
          required 
          placeholder="12.345.678-9"
          className={rutError ? 'border-red-500' : ''}
        />
        {rutError && <p className="text-xs text-red-500">{rutError}</p>}
        <p className="text-[10px] text-slate-400">Formato: XX.XXX.XXX-X</p>
      </div>
      <div className="space-y-2 col-span-2">
        <Label>Giro *</Label>
        <UpperCaseInput 
          value={formData.giro} 
          onChange={(e) => setFormData({ ...formData, giro: e.target.value })} 
          required 
        />
      </div>
      <div className="space-y-2 col-span-2">
        <Label>Dirección *</Label>
        <UpperCaseInput 
          value={formData.direccion} 
          onChange={(e) => setFormData({ ...formData, direccion: e.target.value })} 
          required 
        />
      </div>
      <div className="space-y-2">
        <Label>Comuna *</Label>
        <UpperCaseInput 
          value={formData.comuna} 
          onChange={(e) => setFormData({ ...formData, comuna: e.target.value })} 
          required 
        />
      </div>
      <div className="space-y-2">
        <Label>Ciudad *</Label>
        <UpperCaseInput 
          value={formData.ciudad} 
          onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })} 
          required 
        />
      </div>
      <div className="space-y-2">
        <Label>Nombre Contacto *</Label>
        <UpperCaseInput 
          value={formData.contacto_nombre} 
          onChange={(e) => setFormData({ ...formData, contacto_nombre: e.target.value })} 
          required 
        />
      </div>
      <div className="space-y-2">
        <Label>Teléfono Contacto *</Label>
        <Input 
          value={telefonoDisplay} 
          onChange={handleTelefonoChange} 
          required 
          placeholder="+56 9 1234 5678"
        />
        <p className="text-[10px] text-slate-400">Formato: +56 9 XXXX XXXX</p>
      </div>
    </div>
  );

  if (user?.role !== 'MASTER') {
    return (
      <Layout>
        <div className="text-center py-10">
          <p className="text-slate-500">No tienes permiso para acceder a este módulo</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="clients-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Clientes</h1>
            <p className="text-slate-600 mt-1">Gestión de clientes para órdenes de venta</p>
          </div>
          <Button onClick={openCreateModal} className="bg-primary hover:bg-primary-hover text-white">
            <Plus size={18} className="mr-2" /> Nuevo Cliente
          </Button>
        </div>

        {/* Filtros */}
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
          <div className="flex items-center gap-4">
            <Search size={16} className="text-slate-400" />
            <Input
              placeholder="Buscar por razón social, RUT o ciudad..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="max-w-md h-8"
            />
            {filterText && (
              <Button variant="ghost" size="sm" onClick={() => setFilterText('')}>
                Limpiar
              </Button>
            )}
            <span className="text-xs text-slate-500 ml-auto">{filteredClients.length} de {clients.length}</span>
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Alias</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Razón Social</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">RUT</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Ciudad</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Contacto</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filteredClients.map((client) => (
                <tr key={client.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-semibold text-slate-900">{client.alias || '-'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-slate-400" />
                      <span className="text-slate-700">{client.razon_social}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm">
                    {unirRut(client.rut, client.rut_dv)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-1 text-slate-600">
                      <MapPin size={14} />
                      {client.ciudad}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>
                      <p className="text-slate-900">{client.contacto_nombre}</p>
                      <p className="text-slate-500 flex items-center gap-1">
                        <Phone size={12} /> {client.contacto_telefono}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button onClick={() => openEditModal(client)} variant="ghost" size="sm" className="text-blue-600">
                        <Pencil size={16} />
                      </Button>
                      <Button onClick={() => handleDelete(client.id)} variant="ghost" size="sm" className="text-red-600">
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-slate-400">
                    {loading ? 'Cargando...' : 'No hay clientes registrados'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Create Modal */}
        <Dialog open={isCreateModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsCreateModalOpen(open); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nuevo Cliente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              {renderForm()}
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">
                Crear Cliente
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Modal */}
        <Dialog open={isEditModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsEditModalOpen(open); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Editar Cliente</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEdit} className="space-y-4">
              {renderForm()}
              <div className="space-y-2 pt-4 border-t">
                <Label className="text-red-600">Justificación (obligatoria) *</Label>
                <UpperCaseInput 
                  value={justification} 
                  onChange={(e) => setJustification(e.target.value)} 
                  required 
                  placeholder="Motivo de la edición"
                />
              </div>
              <Button type="submit" className="w-full bg-primary hover:bg-primary-hover text-white">
                Actualizar Cliente
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Clients;
